import React from "react";
import Flatpickr from "react-flatpickr";
import {
  Alert,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Form,
  FormText,
  Input,
  InputGroup,
  InputGroupText,
  Label,
  Row,
  Table,
} from "reactstrap";
import {
  CatalogosState,
  MovimientoBaseConfig,
  MovimientoCabecera,
  MovimientoDetalle,
} from "./MovimientoBase.types";

export type MovimientoFormularioProps = {
  config: MovimientoBaseConfig;
  cabecera: MovimientoCabecera;
  detalles: MovimientoDetalle[];
  catalogos: CatalogosState;
  catalogosCargando: boolean;
  catalogosError: string | null;
  motivosDisponibles: { value: string; label: string }[];
  guardando: boolean;
  alerta: { tipo: "success" | "danger"; mensaje: string } | null;
  numeroRemisionVisible: string;
  timbradoDescripcion: { vigencia: string } | null;
  fechaHoraSeleccionada?: Date;
  onSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
  onReset: () => void;
  onDismissAlerta: () => void;
  onCabeceraChange: (field: keyof MovimientoCabecera, value: string) => void;
  onSucursalTalonarioChange: (value: string) => void;
  onDepositoChange: (value: string) => void;
  onPuntoExpedicionChange: (value: string) => void;
  onDetalleChange: (id: number, field: keyof MovimientoDetalle, value: string) => void;
  onDetalleRemove: (id: number) => void;
  onDetalleAdd: () => void;
  onAbrirClienteModal: () => void;
  onAbrirChoferModal: () => void;
  onAbrirVehiculoModal: () => void;
  onFechaHoraChange: (date: Date | null) => void;
  onPlantillaClick: () => void;
};

const MovimientoFormulario: React.FC<MovimientoFormularioProps> = ({
  config,
  cabecera,
  detalles,
  catalogos,
  catalogosCargando,
  catalogosError,
  motivosDisponibles,
  guardando,
  alerta,
  numeroRemisionVisible,
  timbradoDescripcion,
  fechaHoraSeleccionada,
  onSubmit,
  onReset,
  onDismissAlerta,
  onCabeceraChange,
  onSucursalTalonarioChange,
  onDepositoChange,
  onPuntoExpedicionChange,
  onDetalleChange,
  onDetalleRemove,
  onDetalleAdd,
  onAbrirClienteModal,
  onAbrirChoferModal,
  onAbrirVehiculoModal,
  onFechaHoraChange,
  onPlantillaClick,
}) => (
  <Form onSubmit={onSubmit}>
      <Card className="mb-3">
            <CardHeader className="border-0">
              <div className="d-flex justify-content-between flex-wrap gap-2 align-items-center">
                <div>
                  <h5 className="card-title mb-0">Cabecera</h5>
                  <p className="text-muted mb-0">Definí la información principal antes de detallar los artículos.</p>
                </div>
                <div className="d-flex align-items-center gap-2 flex-wrap justify-content-end">
                  <span className="text-muted small me-2">
                    Los campos marcados con <span className="text-danger">*</span> son obligatorios.
                  </span>
                  <Button color="soft-secondary" type="button" disabled={guardando}>
                    <i className="ri-printer-line align-middle me-1" />
                    Imprimir
                  </Button>
                  <Button color="soft-danger" type="button" disabled={guardando}>
                    <i className="ri-file-pdf-line align-middle me-1" />
                    Descargar PDF
                  </Button>
                  <Button color="soft-info" type="button" disabled={guardando} onClick={onPlantillaClick}>
                    <i className="ri-file-copy-line align-middle me-1" />
                    Listar plantillas
                  </Button>
                  <Button color="secondary" type="button" disabled={guardando} onClick={onReset}>
                    Cancelar
                  </Button>
                  <Button color="primary" type="submit" disabled={guardando}>
                    {guardando ? "Guardando..." : `Guardar ${config.conceptoTitulo.toLowerCase()}`}
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardBody>
              {alerta && (
                <Alert color={alerta.tipo} toggle={onDismissAlerta}>
                  {alerta.mensaje}
                </Alert>
              )}
              <Row className="pt-2 g-4">
                <Col xl={6}>
                  <div className="d-flex flex-column gap-3">
                    <Row className="align-items-center">
                      <Col lg={4}>
                        <Label className="form-label fw-semibold mb-0">Nro. de remisión</Label>
                      </Col>
                      <Col lg={8}>
                        {config.requiereTalonario ? (
                          <Input
                            value={numeroRemisionVisible || ""}
                            readOnly
                            placeholder="000-000-0000000"
                            className="text-end fw-semibold text-muted bg-light"
                          />
                        ) : (
                          <Input
                            value={cabecera.numeroDocumento}
                            placeholder={`Nro. de ${config.conceptoTitulo.toLowerCase()}`}
                            className="text-end"
                            onChange={(e) => onCabeceraChange("numeroDocumento", e.target.value)}
                          />
                        )}
                      </Col>
                    </Row>
                    <Row className="align-items-center">
                      <Col lg={4}>
                        <Label htmlFor="pedido-relacionado" className="form-label fw-semibold mb-0">
                          Pedido relacionado
                        </Label>
                      </Col>
                      <Col lg={8}>
                        <Input
                          id="pedido-relacionado"
                          value={cabecera.pedidoRelacionado}
                          placeholder="Código de pedido asociado"
                          onChange={(e) => onCabeceraChange("pedidoRelacionado", e.target.value)}
                        />
                      </Col>
                    </Row>
                    <Row className="align-items-center">
                      <Col lg={4}>
                        <Label htmlFor="fecha-hora" className="form-label fw-semibold mb-0">
                          Fecha y hora <span className="text-danger">*</span>
                        </Label>
                      </Col>
                      <Col lg={8}>
                        <Flatpickr
                          id="fecha-hora"
                          className="form-control"
                          value={fechaHoraSeleccionada ?? undefined}
                          options={{
                            enableTime: true,
                            time_24hr: true,
                            dateFormat: "Y-m-d H:i",
                            allowInput: true,
                          }}
                          onChange={(dates) => onFechaHoraChange(dates[0] ?? null)}
                        />
                      </Col>
                    </Row>
                    {config.requiereTalonario ? (
                      <>
                        <Row className="align-items-center">
                          <Col lg={4}>
                            <Label className="form-label fw-semibold mb-0">Sucursal / Establecimiento *</Label>
                          </Col>
                          <Col lg={8}>
                            <Input
                              type="select"
                              value={cabecera.sucursalTalonario}
                              onChange={(e) => onSucursalTalonarioChange(e.target.value)}
                            >
                              <option value="">Seleccioná...</option>
                              {catalogos.sucursales.map((sucursal) => (
                                <option key={sucursal.id} value={String(sucursal.id)}>
                                  {sucursal.fuente}
                                </option>
                              ))}
                            </Input>
                            <FormText>Elegí la sucursal/establecimiento que emitirá la remisión.</FormText>
                          </Col>
                        </Row>
                        <Row className="align-items-center">
                          <Col lg={4}>
                            <Label className="form-label fw-semibold mb-0">Depósito para la remisión *</Label>
                          </Col>
                          <Col lg={8}>
                            <Input
                              type="select"
                              value={cabecera.sucursal}
                              onChange={(e) => onDepositoChange(e.target.value)}
                              disabled={catalogos.depositos.length === 0}
                            >
                              <option value="">Seleccioná...</option>
                              {catalogos.depositos.map((deposito) => (
                                <option key={deposito.id} value={String(deposito.id)}>
                                  {deposito.fuente}
                                </option>
                              ))}
                            </Input>
                            <FormText>
                              {catalogos.depositos.length === 0
                                ? "Seleccioná una sucursal para cargar los depósitos disponibles."
                                : "Elegí el depósito que utilizará esta remisión."}
                            </FormText>
                          </Col>
                        </Row>
                        <Row className="align-items-center">
                      <Col lg={4}>
                        <Label className="form-label fw-semibold mb-0">Punto / Caja *</Label>
                      </Col>
                      <Col lg={8}>
                        <Input
                          type="select"
                          value={cabecera.puntoExpedicionId}
                          onChange={(e) => onPuntoExpedicionChange(e.target.value)}
                          disabled={!cabecera.sucursalTalonario || !cabecera.sucursal || catalogos.puntos.length === 0}
                        >
                          <option value="">Seleccioná...</option>
                          {catalogos.puntos.map((punto) => (
                            <option key={punto.id} value={String(punto.id)}>
                              {punto.fuente}
                              {punto.codigoPunto ? ` · ${punto.codigoPunto}` : ""}
                            </option>
                          ))}
                        </Input>
                        <FormText>
                          {cabecera.sucursalTalonario && cabecera.sucursal
                            ? catalogos.puntos.length === 0
                              ? "Seleccioná primero la sucursal para cargar los puntos disponibles."
                              : "Seleccioná la caja o punto de expedición (código PPP)."
                            : "Seleccioná primero la sucursal y el depósito para habilitar este campo."}
                        </FormText>
                      </Col>
                    </Row>
                      </>
                    ) : (
                      <Row className="align-items-center">
                        <Col lg={4}>
                          <Label className="form-label fw-semibold mb-0">Depósito</Label>
                        </Col>
                        <Col lg={8}>
                          <Input
                            type="select"
                            value={cabecera.sucursal}
                            onChange={(e) => onCabeceraChange("sucursal", e.target.value)}
                            disabled={catalogos.depositos.length === 0}
                          >
                            <option value="">Seleccioná...</option>
                            {catalogos.depositos.map((deposito) => (
                              <option key={deposito.id} value={String(deposito.id)}>
                                {deposito.fuente}
                              </option>
                            ))}
                          </Input>
                          <FormText>
                            {catalogosCargando
                              ? "Cargando depósitos..."
                              : config.usarCabeceraDepositos
                                ? "Seleccioná el depósito principal para esta remisión."
                                : "Seleccioná el depósito principal."}
                          </FormText>
                          {catalogosError && <FormText color="danger">{catalogosError}</FormText>}
                        </Col>
                      </Row>
                    )}
                  </div>
                </Col>
                <Col xl={6}>
                  <div className="d-flex flex-column gap-3">
                    <Row className="align-items-center">
                      <Col lg={4}>
                        <Label htmlFor="concepto" className="form-label fw-semibold mb-0">
                          Concepto
                        </Label>
                      </Col>
                      <Col lg={8}>
                        <Input
                          id="concepto"
                          value={cabecera.concepto}
                          readOnly
                          className="bg-light"
                        />
                        <FormText>Concepto fijo definido para este módulo.</FormText>
                      </Col>
                    </Row>
                    {config.usarCabeceraDepositos && (
                      <Row className="align-items-center">
                        <Col lg={4}>
                          <Label className="form-label fw-semibold mb-0">Depósitos</Label>
                        </Col>
                        <Col lg={8}>
                          <InputGroup>
                            <InputGroupText className="fw-semibold">De</InputGroupText>
                            <Input
                              id="deposito-origen"
                              type="select"
                              value={cabecera.depositoOrigenCabecera}
                              onChange={(e) => onCabeceraChange("depositoOrigenCabecera", e.target.value)}
                            >
                              <option value="">Seleccioná...</option>
                              {catalogos.depositos.map((deposito) => (
                                <option key={deposito.id} value={String(deposito.id)}>
                                  {deposito.fuente}
                                </option>
                              ))}
                            </Input>
                            <InputGroupText className="fw-semibold">A</InputGroupText>
                            <Input
                              id="deposito-destino"
                              type="select"
                              value={cabecera.depositoDestinoCabecera}
                              onChange={(e) => onCabeceraChange("depositoDestinoCabecera", e.target.value)}
                            >
                              <option value="">Seleccioná...</option>
                              {catalogos.depositos.map((deposito) => (
                                <option key={deposito.id} value={String(deposito.id)}>
                                  {deposito.fuente}
                                </option>
                              ))}
                            </Input>
                          </InputGroup>
                          <FormText>
                            {catalogosCargando
                              ? "Cargando depósitos..."
                              : "Elegí los depósitos de origen y destino para esta " +
                                config.conceptoTitulo.toLowerCase() +
                                "."}
                          </FormText>
                          {catalogosError && <FormText color="danger">{catalogosError}</FormText>}
                        </Col>
                      </Row>
                    )}
                    {config.mostrarSeccionPersona !== false && (
                      <Row className="align-items-center">
                        <Col lg={4}>
                          <Label className="form-label fw-semibold mb-0">Cliente o persona</Label>
                        </Col>
                        <Col lg={8}>
                          <InputGroup>
                            <Input
                              readOnly
                              value={
                                cabecera.personaNombre
                                  ? `${cabecera.personaNombre}${
                                      cabecera.personaDireccion ? ` · ${cabecera.personaDireccion}` : ""
                                    }`
                                  : "Sin seleccionar"
                              }
                            />
                            <Button color="soft-primary" type="button" className="text-nowrap" onClick={onAbrirClienteModal}>
                              Seleccionar
                            </Button>
                          </InputGroup>
                          <FormText>Seleccioná el cliente responsable del movimiento.</FormText>
                        </Col>
                      </Row>
                    )}
                    <Row className="align-items-center">
                      <Col lg={4}>
                        <Label className="form-label fw-semibold mb-0">Chofer responsable</Label>
                      </Col>
                      <Col lg={8}>
                        <InputGroup>
                          <Input
                            readOnly
                            value={
                              cabecera.choferNombre
                                ? `${cabecera.choferNombre}${
                                    cabecera.choferDocumento ? ` · ${cabecera.choferDocumento}` : ""
                                  }`
                                : "Sin seleccionar"
                            }
                          />
                          <Button color="soft-primary" type="button" className="text-nowrap" onClick={onAbrirChoferModal}>
                            Seleccionar
                          </Button>
                        </InputGroup>
                        <FormText>Seleccioná al chofer responsable del traslado.</FormText>
                      </Col>
                    </Row>
                    <Row className="align-items-center">
                      <Col lg={4}>
                        <Label className="form-label fw-semibold mb-0">Vehículo</Label>
                      </Col>
                      <Col lg={8}>
                        <InputGroup>
                          <Input
                            readOnly
                            value={
                              cabecera.vehiculoDescripcion
                                ? `${cabecera.vehiculoDescripcion}${
                                    cabecera.vehiculoMatricula ? ` · ${cabecera.vehiculoMatricula}` : ""
                                  }`
                                : "Sin seleccionar"
                            }
                          />
                          <Button color="soft-primary" type="button" className="text-nowrap" onClick={onAbrirVehiculoModal}>
                            Seleccionar
                          </Button>
                        </InputGroup>
                        <FormText>Seleccioná el vehículo involucrado en la remisión.</FormText>
                      </Col>
                    </Row>
                    <Row className="align-items-start">
                      <Col lg={4}>
                        <Label className="form-label fw-semibold mb-0">Referencia / Comentarios</Label>
                      </Col>
                      <Col lg={8}>
                        <Input
                          type="textarea"
                          rows={3}
                          placeholder="Notas internas o instrucciones adicionales"
                          value={cabecera.referencia}
                          onChange={(e) => onCabeceraChange("referencia", e.target.value)}
                        />
                      </Col>
                    </Row>
                  </div>
                </Col>
              </Row>
              <Row className="g-4 align-items-start mt-1">
                {motivosDisponibles.length > 0 && (
                  <Col xs={12}>
                    <Label htmlFor="motivo" className="form-label fw-semibold">
                      Motivo
                    </Label>
                    <Input
                      id="motivo"
                      type="select"
                      value={cabecera.motivo}
                      onChange={(e) => onCabeceraChange("motivo", e.target.value)}
                    >
                      <option value="">Seleccioná...</option>
                      {motivosDisponibles.map((opcion) => (
                        <option key={opcion.value} value={opcion.value}>
                          {opcion.label}
                        </option>
                      ))}
                    </Input>
                  </Col>
                )}
              </Row>
            </CardBody>
          </Card>

      <Card className="mb-3">
        <CardHeader className="border-0">
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
            <div>
              <h5 className="card-title mb-0">Detalle</h5>
              <p className="text-muted mb-0">Salida y entrada por depósito, junto con los artículos movidos.</p>
            </div>
            <Button color="soft-primary" onClick={onDetalleAdd}>
              <i className="ri-add-line align-bottom me-1" />
              Agregar línea
            </Button>
          </div>
        </CardHeader>
        <CardBody className="p-0 pb-4">
          <div className="table-responsive">
            <Table className="mb-0 align-middle table-nowrap">
              <thead className="table-light">
                <tr>
                  <th style={{ width: "240px" }}>{config.usarCabeceraDepositos ? "Depósitos" : "Depósito"}</th>
                  <th style={{ width: "140px" }}>Código</th>
                  <th style={{ width: "120px" }}>Unidad</th>
                  <th style={{ width: "120px" }}>Cantidad</th>
                  <th>Descripción artículo</th>
                  <th style={{ width: "60px" }}></th>
                </tr>
              </thead>
              <tbody>
                {detalles.map((detalle) => (
                  <tr key={detalle.id}>
                    <td>
                      {config.usarCabeceraDepositos ? (
                        <InputGroup>
                          <InputGroupText className="fw-semibold">De</InputGroupText>
                          <Input
                            type="select"
                            value={detalle.depositoSalida}
                            onChange={(e) => onDetalleChange(detalle.id, "depositoSalida", e.target.value)}
                          >
                            <option value="">Depósito origen</option>
                            {catalogos.depositos.map((deposito) => (
                              <option key={deposito.id} value={String(deposito.id)}>
                                {deposito.fuente}
                              </option>
                            ))}
                          </Input>
                          <InputGroupText className="fw-semibold">A</InputGroupText>
                          <Input
                            type="select"
                            value={detalle.depositoEntrada}
                            onChange={(e) => onDetalleChange(detalle.id, "depositoEntrada", e.target.value)}
                          >
                            <option value="">Depósito destino</option>
                            {catalogos.depositos.map((deposito) => (
                              <option key={deposito.id} value={String(deposito.id)}>
                                {deposito.fuente}
                              </option>
                            ))}
                          </Input>
                        </InputGroup>
                      ) : (
                        <Input
                          type="select"
                          value={detalle.depositoSalida}
                          onChange={(e) => onDetalleChange(detalle.id, "depositoSalida", e.target.value)}
                        >
                          <option value="">Seleccioná...</option>
                          {catalogos.depositos.map((deposito) => (
                            <option key={deposito.id} value={String(deposito.id)}>
                              {deposito.fuente}
                            </option>
                          ))}
                        </Input>
                      )}
                    </td>
                    <td>
                      <Input
                        value={detalle.codigo}
                        list="stock-articulos-options"
                        placeholder="Código / SKU"
                        onChange={(e) => onDetalleChange(detalle.id, "codigo", e.target.value)}
                      />
                    </td>
                    <td>
                      <Input
                        value={detalle.unidad}
                        placeholder="Unidad"
                        onChange={(e) => onDetalleChange(detalle.id, "unidad", e.target.value)}
                      />
                    </td>
                    <td>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={detalle.cantidad}
                        placeholder="0"
                        onChange={(e) => onDetalleChange(detalle.id, "cantidad", e.target.value)}
                      />
                    </td>
                    <td>
                      <Input
                        value={detalle.descripcion}
                        placeholder="Detalle o notas"
                        onChange={(e) => onDetalleChange(detalle.id, "descripcion", e.target.value)}
                      />
                    </td>
                    <td>
                      <Button
                        size="sm"
                        color="soft-danger"
                        disabled={detalles.length === 1}
                        onClick={() => onDetalleRemove(detalle.id)}
                      >
                        <i className="ri-delete-bin-5-line" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </CardBody>
      </Card>
      {catalogos.articulos.length > 0 && (
        <datalist id="stock-articulos-options">
          {catalogos.articulos.map((articulo) => (
            <option key={articulo.id} value={articulo.sku ?? String(articulo.id)}>
              {articulo.descripcion}
            </option>
          ))}
        </datalist>
      )}
    </Form>
);

export default MovimientoFormulario;
